import { supabase } from '@/integrations/supabase/client';
import { Project } from '@/types/project';
import { mapDatabaseProjectToProject } from '@/utils/projectMappers';
import { saveMaterials, deleteProjectMaterials } from './resourceHelpers';
import { saveLabor, deleteProjectLabor } from './resourceHelpers';
import { dataRefresh } from '@/lib/dataRefresh';

// Update a project
export const updateProject = async (id: string | undefined, project: Partial<Project>): Promise<Project | null> => {
  // Don't proceed if no ID is provided
  if (!id) {
    console.error('No project ID provided for update');
    return null;
  }
  try {
    console.log('Updating project with data:', project);
    
    // Only update the fields that are allowed in the projects table (without metadata)
    const dbProject: any = {};
    
    if (project.name !== undefined) dbProject.title = project.name;
    if (project.projectNumber !== undefined) dbProject.project_number = project.projectNumber;
    if (project.pic !== undefined) dbProject.pic = project.pic;
    if (project.supervisor !== undefined) dbProject.supervisor = project.supervisor;
    if (project.workStatus !== undefined) dbProject.status = project.workStatus;
    if (project.progress !== undefined) dbProject.progress = project.progress;
    if (project.startDate !== undefined) dbProject.start_date = project.startDate;
    if (project.endDate !== undefined) dbProject.end_date = project.endDate;
    if (project.description !== undefined) dbProject.description = project.description;
    if (project.projectType !== undefined) dbProject.project_type = project.projectType;
    if (project.location !== undefined) dbProject.location = project.location;
    if (project.budgetRAB !== undefined) dbProject.budget_rab = project.budgetRAB;
    
    // Always update the updated_at timestamp
    dbProject.updated_at = new Date().toISOString();
    
    console.log('Mapped database project for update:', dbProject);
    
    const { data, error } = await supabase
      .from('projects')
      .update(dbProject)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating project:', error);
      return null;
    }
    
    console.log('Project updated successfully:', data);
    
    // Update materials if provided
    if (project.materials !== undefined) {
      // First, delete existing materials for this project
      const deleteMatSuccess = await deleteProjectMaterials(id);
      if (!deleteMatSuccess) {
        console.error('Failed to delete existing materials, aborting material update');
        throw new Error('Failed to update materials - could not remove old data');
      }
      
      // Then insert the new materials
      if (project.materials.length > 0) {
        const saveMatSuccess = await saveMaterials(id, project.materials);
        if (!saveMatSuccess) {
          throw new Error('Failed to save new materials');
        }
      }
    }
    
    // Update labor if provided
    if (project.labor !== undefined) {
      // First, delete existing labor for this project
      const deleteLaborSuccess = await deleteProjectLabor(id);
      if (!deleteLaborSuccess) {
        console.error('Failed to delete existing labor, aborting labor update');
        throw new Error('Failed to update labor - could not remove old data');
      }
      
      // Then insert the new labor
      if (project.labor.length > 0) {
        const saveLaborSuccess = await saveLabor(id, project.labor);
        if (!saveLaborSuccess) {
          throw new Error('Failed to save new labor');
        }
      }
    }
    
    // Re-fetch materials and labor with correct sort_order to return accurate data
    const [materialsResult, laborResult] = await Promise.all([
      supabase
        .from('materials')
        .select('*')
        .eq('project_id', id)
        .order('sort_order', { ascending: true }),
      supabase
        .from('labors')
        .select('*')
        .eq('project_id', id)
        .order('sort_order', { ascending: true })
    ]);

    const databaseProject = {
      ...data,
      materials: materialsResult.data || [],
      labors: laborResult.data || [],
      supervisor: (data as any).supervisor || '',
      schedule_pdf_url: (data as any).schedule_pdf_url || '',
      metadata: {}
    };
    
    // Convert the database project back to our local format
    const result = mapDatabaseProjectToProject(databaseProject);
    
    // Trigger refresh for projects
    await dataRefresh.trigger('projects');
    
    return result;
  } catch (error) {
    console.error('Error updating project:', error);
    return null;
  }
};
